/**
 * Phase 13 Beat 4 — Originally-posted-date helpers.
 *
 * The employer's `originally_posted_at` is a first-class Job field. It is
 * different from `discovered_at` (when KGS captured the job) and
 * `first_captured_at` (when the JD snapshot was taken). We NEVER infer this
 * date — if it's missing we surface "Posted: Unknown".
 */

/**
 * Format an ISO datetime as a human-readable "Posted: …" string.
 * Rules (spec §10 in the wireframe):
 *   < 1h                        → "12m ago"
 *   1h - 24h same day           → "5h ago"
 *   Yesterday                   → "Yesterday"
 *   2-6 days                    → "3 days ago"
 *   7-13 days                   → "1 week ago"
 *   14-27 days                  → "2 weeks ago"
 *   28-59 days                  → "1 month ago"
 *   >= 60 days                  → absolute "Feb 3, 2026"
 *   null / undefined / invalid  → "Posted: Unknown"
 */
export function formatPostedAge(iso) {
  if (!iso) return "Posted: Unknown";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "Posted: Unknown";
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  const hrs = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  if (diffMs < 0) return "Posted just now";
  if (mins < 1) return "Posted just now";
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days === 1) return "Yesterday";
  if (days <= 6) return `${days} days ago`;
  if (days <= 13) return "1 week ago";
  if (days <= 27) return `${Math.floor(days / 7)} weeks ago`;
  if (days <= 59) return "1 month ago";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Absolute local string for the tooltip: "Feb 12, 2026 · 3:42 PM". */
export function formatPostedAbsolute(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

/** Query params for the "Today / Last 24h / Last 7d / Last 30d / All" chips. */
export function postedRangeQuery(preset) {
  const now = new Date();
  const daysAgo = (n) => {
    const d = new Date(now); d.setDate(now.getDate() - n); return d.toISOString();
  };
  switch (preset) {
    case "today": {
      const start = new Date(now); start.setHours(0, 0, 0, 0);
      return { posted_after: start.toISOString() };
    }
    case "24h":  return { posted_after: daysAgo(1) };
    case "7d":   return { posted_after: daysAgo(7) };
    case "30d":  return { posted_after: daysAgo(30) };
    case "all":
    default:     return {};
  }
}

export const POSTED_PRESETS = [
  { key: "today", label: "Today" },
  { key: "24h",   label: "Last 24h" },
  { key: "7d",    label: "Last 7 days" },
  { key: "30d",   label: "Last 30 days" },
  { key: "all",   label: "All" },
];

export const SORT_OPTIONS = [
  { key: "match",       label: "Match %" },
  { key: "posted_desc", label: "Newest Posted" },
  { key: "posted_asc",  label: "Oldest Posted" },
];
